import React, { useState, useEffect } from 'react';
import { Save, TrendingUp, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import PageHeader from '../../components/admin/PageHeader';

interface Campaign {
  id: string;
  name: string;
  target_amount: number;
  raised_amount: number;
  investor_count: number;
  minimum_ticket: number;
  investment_type: string;
  horizon_years: string;
  sector: string;
  fund_usage: string[];
  partner_cabinet: string;
  legal_notice: string;
  is_active: boolean;
}

const FundraisingCampaign: React.FC = () => {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    raised_amount: 0,
    investor_count: 0,
    minimum_ticket: 5000,
    is_active: true,
  });

  useEffect(() => {
    fetchCampaign();
  }, []);

  const fetchCampaign = async () => {
    try {
      const { data, error } = await supabase
        .from('fundraising_campaigns')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) throw error;

      if (data) {
        setCampaign(data);
        setFormData({
          raised_amount: data.raised_amount,
          investor_count: data.investor_count,
          minimum_ticket: data.minimum_ticket,
          is_active: data.is_active,
        });
      }
    } catch (error) {
      console.error('Error fetching campaign:', error);
      toast.error('Erreur lors du chargement de la campagne');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!campaign) return;

    setSaving(true);

    try {
      const { error } = await supabase
        .from('fundraising_campaigns')
        .update({
          raised_amount: formData.raised_amount,
          investor_count: formData.investor_count,
          minimum_ticket: formData.minimum_ticket,
          is_active: formData.is_active,
          updated_at: new Date().toISOString(),
        })
        .eq('id', campaign.id);

      if (error) throw error;

      toast.success('Campagne mise à jour avec succès');
      fetchCampaign();
    } catch (error) {
      console.error('Error updating campaign:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const calculatePercentage = () => {
    if (!campaign) return 0;
    return Math.min((formData.raised_amount / campaign.target_amount) * 100, 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 flex items-start">
          <AlertCircle size={24} className="text-yellow-600 mr-3 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-yellow-900 mb-1">Aucune campagne trouvée</h3>
            <p className="text-yellow-800">Veuillez créer une campagne de levée de fonds.</p>
          </div>
        </div>
      </div>
    );
  }

  const percentage = calculatePercentage();

  return (
    <div className="p-6">
      <PageHeader
        title="Gestion de la Levée de Fonds"
        subtitle="Mettez à jour les informations de la campagne en temps réel"
      />

      {/* Progress Overview */}
      <div className="mb-8 bg-gradient-to-br from-primary-50 to-white rounded-xl shadow-lg p-6 border border-primary-100">
        <div className="flex items-center mb-4">
          <TrendingUp size={24} className="text-primary-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Aperçu de la progression</h3>
        </div>

        <div className="mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Progression</span>
            <span className="font-bold text-primary-600">{percentage.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-gradient-to-r from-primary-500 to-primary-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${percentage}%` }}
            ></div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-sm text-gray-600 mb-1">Montant levé</div>
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(formData.raised_amount)}</div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-sm text-gray-600 mb-1">Objectif</div>
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(campaign.target_amount)}</div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-sm text-gray-600 mb-1">Investisseurs</div>
            <div className="text-2xl font-bold text-gray-900">{formData.investor_count}</div>
          </div>
        </div>
      </div>

      {/* Edit Form */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Modifier les données de la campagne</h3>
          <p className="text-sm text-gray-600 mt-1">Ces modifications seront visibles immédiatement sur le site</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Montant levé (€) *
              </label>
              <input
                type="number"
                value={formData.raised_amount}
                onChange={(e) => setFormData({ ...formData, raised_amount: Number(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0"
                min="0"
                step="1000"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre d'investisseurs *
              </label>
              <input
                type="number"
                value={formData.investor_count}
                onChange={(e) => setFormData({ ...formData, investor_count: Number(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0"
                min="0"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ticket minimum (€) *
              </label>
              <input
                type="number"
                value={formData.minimum_ticket}
                onChange={(e) => setFormData({ ...formData, minimum_ticket: Number(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="5000"
                min="0"
                step="1000"
                required
              />
            </div>

            <div className="flex items-center">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                <span className="ml-3 text-sm font-medium text-gray-700">
                  Campagne active
                </span>
              </label>
            </div>
          </div>

          {/* Campaign Info (Read-only) */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-3">Informations de la campagne (lecture seule)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Nom:</span>
                <span className="ml-2 font-medium text-gray-900">{campaign.name}</span>
              </div>
              <div>
                <span className="text-gray-600">Objectif:</span>
                <span className="ml-2 font-medium text-gray-900">{formatCurrency(campaign.target_amount)}</span>
              </div>
              <div>
                <span className="text-gray-600">Type:</span>
                <span className="ml-2 font-medium text-gray-900">{campaign.investment_type}</span>
              </div>
              <div>
                <span className="text-gray-600">Horizon:</span>
                <span className="ml-2 font-medium text-gray-900">{campaign.horizon_years}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Les modifications sont appliquées immédiatement
            </p>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <Save size={20} className="mr-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FundraisingCampaign;
